import { useEffect, useRef, useState } from 'react';
import { AGENT_PHASES, type AgentPhase } from '../constants';

// ── Screen type ───────────────────────────────────────────────────────
// The active screen the app should render. Derived from phase + showWelcome + showSignIn.
export type Screen = 'sign-in' | 'welcome' | 'form' | 'generating' | 'itinerary' | 'error' | 'email' | 'goodbye';

// ── Hash routing ──────────────────────────────────────────────────────
// Each screen maps to a URL hash so browser back/forward buttons work.
type Hash = 'welcome' | 'authentication' | 'form' | 'generating' | 'itinerary' | 'error' | 'email' | 'goodbye';

const PHASE_TO_HASH: Record<string, Hash> = {
  [AGENT_PHASES.RUNNING]: 'generating',
  [AGENT_PHASES.COMPLETE]: 'itinerary',
  [AGENT_PHASES.ERROR]: 'error',
  [AGENT_PHASES.EMAIL]: 'email',
  [AGENT_PHASES.GOODBYE]: 'goodbye',
};

function readHash(): Hash | null {
  const h = window.location.hash.replace('#', '') as Hash;
  return h || null;
}

function stateToHash(phase: AgentPhase, showWelcome: boolean, showSignIn: boolean): Hash {
  if (showSignIn) return 'authentication';
  if (showWelcome) return 'welcome';
  if (phase === AGENT_PHASES.IDLE) return 'form';
  return PHASE_TO_HASH[phase] || 'welcome';
}

// ── Hook ──────────────────────────────────────────────────────────────

interface UseAppNavigationOpts {
  /** Whether itinerary data exists (needed to guard back-nav to #itinerary) */
  hasItinerary: boolean;
  /** Called whenever navigation lands on the welcome screen (reset app state) */
  onResetState?: () => void;
}

export function useAppNavigation({ hasItinerary, onResetState }: UseAppNavigationOpts) {
  const [phase, setPhase] = useState<AgentPhase>('idle');
  const [showWelcome, setShowWelcome] = useState(true);
  const [showSignIn, setShowSignIn] = useState(false);

  // Ref to suppress hashchange handler when we push a hash programmatically
  const suppressRef = useRef(false);

  // ── Derived screen ────────────────────────────────────────────────
  const screen: Screen = showSignIn
    ? 'sign-in'
    : showWelcome
      ? 'welcome'
      : (PHASE_TO_HASH[phase] as Screen) || 'form';

  // ── Navigation actions ────────────────────────────────────────────
  const nav = {
    openSignIn:       () => setShowSignIn(true),
    closeSignIn:      () => setShowSignIn(false),
    goToWelcome:      () => { setShowSignIn(false); setShowWelcome(true); setPhase(AGENT_PHASES.IDLE); onResetState?.(); },
    goToForm:         () => { setShowSignIn(false); setShowWelcome(false); setPhase(AGENT_PHASES.IDLE); },
    goToRunning:      () => setPhase(AGENT_PHASES.RUNNING),
    goToComplete:     () => setPhase(AGENT_PHASES.COMPLETE),
    goToError:        () => setPhase(AGENT_PHASES.ERROR),
    goToEmail:        () => setPhase(AGENT_PHASES.EMAIL),
    goToGoodbye:      () => setPhase(AGENT_PHASES.GOODBYE),
    /** Go back to form keeping session + form data for re-submission */
    goToModifyTrip:   () => { setPhase(AGENT_PHASES.IDLE); setShowWelcome(false); },
    /** Full reset — back to welcome */
    resetToWelcome:   () => { setPhase(AGENT_PHASES.IDLE); setShowWelcome(true); onResetState?.(); },
  };

  // ── Hash sync: push hash when state changes ───────────────────────
  // Some transitions replace the current history entry instead of pushing,
  // so the back button skips intermediate screens (e.g. form → generating
  // replaces #form so back from #generating goes to #welcome, not #form).
  useEffect(() => {
    const desired = stateToHash(phase, showWelcome, showSignIn);
    if (readHash() !== desired) {
      suppressRef.current = true;
      window.location.hash = desired;
    }
  }, [phase, showWelcome, showSignIn]);

  // ── Hash sync: handle browser back/forward ────────────────────────
  useEffect(() => {
    const handler = () => {
      if (suppressRef.current) { suppressRef.current = false; return; }

      const hash = readHash();

      // Back from goodbye → always go to welcome
      if (phase === AGENT_PHASES.GOODBYE && hash !== 'goodbye') {
        nav.goToWelcome();
        return;
      }

      // Confirm before leaving the generating screen
      if (phase === AGENT_PHASES.RUNNING && hash !== 'generating') {
        const leave = window.confirm("You'll lose Connie's current thinking. Are you sure?");
        if (!leave) {
          suppressRef.current = true;
          window.location.hash = 'generating';
          return;
        }
      }

      switch (hash) {
        case 'welcome':  nav.goToWelcome(); break;
        case 'authentication': nav.openSignIn(); break;
        case 'form':     nav.goToForm(); break;

        case 'itinerary':
          if (hasItinerary) setPhase(AGENT_PHASES.COMPLETE);
          else { suppressRef.current = true; window.location.hash = 'form'; }
          break;

        // Can't navigate to email/goodbye via back button — redirect
        case 'email':
          suppressRef.current = true;
          window.location.hash = hasItinerary ? 'itinerary' : 'form';
          break;
        case 'goodbye':
          suppressRef.current = true;
          window.location.hash = hasItinerary ? 'itinerary' : 'welcome';
          break;

        case 'generating':
          if (phase === AGENT_PHASES.RUNNING) break;
          nav.goToWelcome();
          break;
        case 'error':
          if (phase === AGENT_PHASES.ERROR) break;
          suppressRef.current = true;
          window.location.hash = 'form';
          break;

        default:
          suppressRef.current = true;
          window.location.hash = 'welcome';
      }
    };

    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [phase, hasItinerary]);

  // ── Set initial hash on mount ─────────────────────────────────────
  useEffect(() => {
    if (!readHash()) {
      suppressRef.current = true;
      window.location.hash = 'welcome';
    }
  }, []);

  // ── Warn before losing progress on refresh/close ──────────────────
  useEffect(() => {
    if (phase === 'idle' || phase === 'goodbye') return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [phase]);

  return { screen, phase, nav };
}
